import { motion } from 'framer-motion';
import { card3DHover } from '@/animations/variants';

interface USPCardProps {
  title: string;
  description: string;
  image?: string;
}

export default function USPCard({ title, description, image }: USPCardProps) {
  return (
    <motion.div
      className="bg-surface border border-border rounded-xl overflow-hidden hover:border-primary/50 hover:shadow-lg transition-all duration-normal h-full"
      variants={card3DHover}
      whileHover="whileHover"
      style={{
        transformStyle: "preserve-3d",
      }}
    >
      {/* Image Section */}
      {image && (
        <div className="h-32 sm:h-40 lg:h-48 bg-gradient-to-br from-primary/20 to-secondary/20 relative overflow-hidden">
          <img 
            src={image} 
            alt={title}
            className="w-full h-full object-cover transition-transform duration-slow hover:scale-110"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.parentElement!.classList.add('flex', 'items-center', 'justify-center');
              target.style.display = 'none';
              target.parentElement!.innerHTML = `
                <div class="text-center text-textSecondary">
                  <div class="text-4xl sm:text-4xl mb-2">📅</div>
                  <div class="text-xs sm:text-sm">Image placeholder</div>
                </div>
              `;
            }}
          />
          
          {/* Overlay Gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-surface/80 to-transparent" />
        </div>
      )}

      {/* Content */}
      <div className="p-4 sm:p-6 lg:p-8 flex flex-col h-full">
        <h3 className="text-lg sm:text-2xl font-heading font-semibold text-textPrimary mb-3 sm:mb-4">
          {title}
        </h3>
        
        <p className="text-textSecondary text-sm sm:text-base leading-relaxed mb-4 sm:mb-6 flex-grow">
          {description}
        </p>

        {/* Learn More Link */}
        <motion.button
          className="inline-flex items-center text-primary hover:text-primary/80 font-medium text-xs sm:text-sm transition-colors self-start"
          whileHover={{ x: 5 }}
          transition={{ duration: 0.2 }}
        >
          Learn More
          <svg 
            className="w-3 h-3 sm:w-4 sm:h-4 ml-1" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M9 5l7 7-7 7" 
            />
          </svg>
        </motion.button>
      </div>

      {/* Glow Effect on Hover */}
      <motion.div
        className="absolute inset-0 rounded-xl opacity-0 pointer-events-none"
        whileHover={{ 
          opacity: 1,
          boxShadow: "0 0 30px rgba(59, 164, 246, 0.3)",
        }}
        transition={{ duration: 0.3 }}
      />
    </motion.div>
  );
}